import React, { useState } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {
  InputLabel,
  Input,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import FormControl, { useFormControl } from "@mui/material/FormControl";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { AuthContext, DialogContext, JoySnackBarContext } from "./context";
import { styled } from "@mui/system"; // Import styled from @mui/system

// Create custom styles using styled
const DialogActionsMobile = styled(DialogActions)(({ theme }) => ({
  marginTop: theme.spacing(3), // Adjust as needed
  display: "flex",
  justifyContent: "center",
}));

const config = require("../config/config");

export default function SignInDialog({ login }) {
  const { DialogDispatcher } = React.useContext(DialogContext);
  const { authState, AuthDispatcher } = React.useContext(AuthContext);
  const { JoySnackBarDispatcher } = React.useContext(JoySnackBarContext);

  const [showPassword, setShowPassword] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");

  const [isIdentifierError, setIsIdentifierError] = useState(false);
  const [isPasswordError, setIsPasswordError] = useState(false);
  const [identifierDialogueAlertMessage, setIdentifierDialogueAlertMessage] =
    useState("");
  const [passwordDialogueAlertMessage, setPasswordDialogueAlertMessage] =
    useState("");

  const theme = useTheme();

  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleForgotPassword = () => {
    DialogDispatcher("OPEN", { forgotPassword: true, login: false });
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClose = () => {
    DialogDispatcher("OPEN", { login: false });
  };

  const MyFormHelperText = () => {
    const { focused } = useFormControl() || {};

    const helperText = React.useMemo(() => {
      if (focused) {
        return "If phone, enter without country code";
      }

      return "";
    }, [focused]);

    return <FormHelperText>{helperText}</FormHelperText>;
  };

  const handleSignIn = async (identifier, password) => {
    try {
      const response = await axios.post(
        `${config.baseURL}/auth/login`,
        {
          identifier,
          password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Username:", identifier);
      console.log("Password:", password);
      console.log("API Response:", response.data);

      // Assuming the response contains the userId and role, update the auth state
      AuthDispatcher("LOGGED-IN", {
        userId: response.data.username,
        role: response.data.role,
        token: response.data.token,
      });

      console.log(authState);
      setIsIdentifierError(false);

      JoySnackBarDispatcher("OPEN", {
        vertical: "top",
        horizontal: "right",
        message: response.data.snackAlertMessage,
        color: "success",
        size: "md",
        variant: "soft",
        iconName: "LoginSuccessful",
        iconColor: "success",
      });

      handleClose();
    } catch (error) {
      console.error("Error during sign in:", error);

      if (error.response.status === 404) {
        console.log(error.response.data.dialogueAlertMessage);
        console.log(error.response.data.snackAlertMessage);
        setIsIdentifierError(true);
        setIdentifierDialogueAlertMessage(
          error.response.data.dialogueAlertMessage
        );

        JoySnackBarDispatcher("OPEN", {
          vertical: "top",
          horizontal: "right",
          message: error.response.data.snackAlertMessage,
          color: "danger",
          size: "md",
          variant: "soft",
          iconName: "UserNotFound",
          iconColor: "danger",
        });
      }

      if (error.response.status === 401) {
        setIsIdentifierError(false);
        console.log(error.response.data.dialogueAlertMessage);
        console.log(error.response.data.snackAlertMessage);
        setPasswordDialogueAlertMessage(
          error.response.data.dialogueAlertMessage
        );
        setIsPasswordError(true);
        JoySnackBarDispatcher("OPEN", {
          vertical: "top",
          horizontal: "right",
          message: error.response.data.snackAlertMessage,
          color: "danger",
          size: "md",
          variant: "soft",
          iconName: "WrongPassword",
          iconColor: "danger",
        });
      }
    }
  };

  return (
    <div>
      <Dialog open={login} onClose={handleClose} fullScreen={fullScreen}>
        <DialogTitle>Sign In</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter your details to sign in.
          </DialogContentText>

          <FormControl
            required
            error={isIdentifierError} // COMPLEX LOGIC
            color="success" // COMPLEX LOGIC
            fullWidth
            sx={{ mt: 1, mb: 0.5 }}
            variant="standard"
          >
            <InputLabel htmlFor="standard-adornment-username">
              username/email/phone
            </InputLabel>
            <Input
              id="standard-adornment-username"
              color="success" // COMPLEX LOGIC
              type="text"
              // startAdornment={
              //   <InputAdornment position="start">
              //     <AccountCircle sx={{ color: "green" }} />
              //   </InputAdornment>
              // }
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <FormHelperText id="standard-weight-helper-text">
              {identifierDialogueAlertMessage}
            </FormHelperText>
            <MyFormHelperText />
          </FormControl>

          <FormControl
            required
            error={isPasswordError} // COMPLEX LOGIC
            color="success" // COMPLEX LOGIC
            fullWidth
            sx={{ mt: 1, mb: 0.5 }}
            variant="standard"
          >
            <InputLabel htmlFor="standard-adornment-password">
              Password
            </InputLabel>
            <Input
              id="standard-adornment-password"
              color="success" // COMPLEX LOGIC
              type={showPassword ? "text" : "password"}
              // startAdornment={
              //   <InputAdornment position="start">
              //     <KeyIcon sx={{ color: "green" }} />
              //   </InputAdornment>
              // }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSignIn(identifier, password);
                }
              }}
            />
            <FormHelperText id="standard-weight-helper-text">
              {passwordDialogueAlertMessage}
            </FormHelperText>
          </FormControl>

          <Link href="#" variant="body2" onClick={() => handleForgotPassword()}>
            Forgot password?
          </Link>
          {fullScreen ? (
            <DialogActionsMobile>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClose}
                sx={{ mr: (theme) => theme.spacing(3) }} // Adds margin-right using theme spacing
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleSignIn(identifier, password)}
              >
                Sign In
              </Button>
            </DialogActionsMobile>
          ) : (
            <DialogActions>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClose}
                sx={{ mr: (theme) => theme.spacing(1) }} // Adds margin-right using theme spacing
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleSignIn(identifier, password)}
              >
                Sign In
              </Button>
            </DialogActions>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
